import { Handle, Position } from 'reactflow';
import { Mic } from 'lucide-react';
import { Badge } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { VoiceTestData } from '@simcheck/shared-types';

interface VoiceTestNodeData {
  label?: string;
  voiceTest?: Partial<VoiceTestData>;
}

export default function VoiceTestNode({
  data,
  selected,
}: {
  data: VoiceTestNodeData;
  selected?: boolean;
}) {
  const vt = data.voiceTest;

  return (
    <div className={cn(
      "min-w-[170px] rounded-lg",
      "bg-card",
      "border-2 transition-all duration-fast",
      selected
        ? "border-category-voice shadow-glow-focus"
        : "border-category-voice/50 dark:border-category-voice/30 shadow-md hover:shadow-lg"
    )}>
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !border-2 !bg-category-voice !border-category-voice"
        style={{ top: '-6px' }}
      />

      {/* Node Header */}
      <div className="flex items-center gap-2 p-3 border-b border-border dark:border-border-subtle">
        <div className="w-6 h-6 rounded bg-category-voice/10 dark:bg-category-voice/20 flex items-center justify-center">
          <Mic className="w-3.5 h-3.5 text-category-voice" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground">
            Voice Test
          </div>
          <div className="text-xs text-muted-foreground/70 font-mono truncate">
            {vt?.kind || 'Not configured'}
          </div>
        </div>
        {vt?.expectedBearer && (
          <Badge variant="outline" className="border-category-voice text-category-voice">
            {vt.expectedBearer}
          </Badge>
        )}
      </div>

      {/* Node Body */}
      <div className="p-3 space-y-2">
        {vt?.dialNumber && (
          <div className="text-xs">
            <span className="text-muted-foreground font-medium">Dial:</span>
            <span className="ml-1 text-foreground font-mono truncate">{vt.dialNumber}</span>
          </div>
        )}

        {vt?.dtmfDigits && (
          <div className="text-xs">
            <span className="text-muted-foreground font-medium">DTMF:</span>
            <span className="ml-1 text-foreground font-mono">{vt.dtmfDigits}</span>
          </div>
        )}

        {vt?.storeAs && (
          <div className="text-xs">
            <span className="text-category-voice font-medium font-mono">
              → ${vt.storeAs}
            </span>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-between items-center pt-2 border-t border-border dark:border-border-subtle">
          <span className="text-xs text-muted-foreground/70">
            {vt?.durationSec ? `${vt.durationSec}s` : vt?.timeout ? `${vt.timeout}ms` : ''}
          </span>
          {vt?.codecMode && vt.codecMode !== 'auto' && (
            <span className="text-xs text-muted-foreground/70 uppercase">
              {vt.codecMode}
            </span>
          )}
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !border-2 !bg-category-voice !border-category-voice"
        style={{ bottom: '-6px' }}
      />
    </div>
  );
}
